/*
 * Project: bellcloud
 * File: apps/frontend/src/utils/ringBuffer.ts
 * Description: Optimized ring buffer with 2^n size for fast bit-mask operations (Python pattern)
 */

export class RingBuffer {
  private buffer: Uint8Array;
  private readonly capacity: number;
  private readonly mask: number; // Bit mask for fast modulo (Python pattern)
  private head = 0;
  private tail = 0;
  private droppedBytes = 0; // Track dropped bytes for monitoring

  constructor(capacity: number) {
    // Round up to nearest power of 2 for bit-mask optimization (Python pattern)
    const minCapacity = Math.max(1024, capacity);
    this.capacity = this.nextPowerOf2(minCapacity);
    this.mask = this.capacity - 1; // For fast modulo: (index) & mask
    this.buffer = new Uint8Array(this.capacity);
  }

  /**
   * Round up to next power of 2 (Python pattern: 2^n buffer size)
   */
  private nextPowerOf2(n: number): number {
    let power = 1;
    while (power < n) {
      power *= 2;
    }
    return power;
  }

  /**
   * Get number of bytes available to read
   */
  available(): number {
    // Python pattern: calculate length from head/tail
    if (this.head >= this.tail) {
      return this.head - this.tail;
    } else {
      return this.capacity - this.tail + this.head;
    }
  }

  /**
   * Check if buffer is empty (Python pattern: head == tail)
   */
  isEmpty(): boolean {
    return this.head === this.tail;
  }

  /**
   * Check if buffer is full (Python pattern: nextHead == tail)
   */
  isFull(): boolean {
    return ((this.head + 1) & this.mask) === this.tail;
  }

  /**
   * Get buffer usage percentage (0-100)
   */
  getUsage(): number {
    return (this.available() / this.capacity) * 100;
  }

  /**
   * Get total dropped bytes count
   */
  getDroppedBytes(): number {
    return this.droppedBytes;
  }

  /**
   * Reset dropped bytes counter
   */
  resetDroppedBytes(): void {
    this.droppedBytes = 0;
  }

  clear(): void {
    this.head = 0;
    this.tail = 0;
    this.droppedBytes = 0;
  }

  pushBytes(bytes: Uint8Array): number {
    let pushed = 0;
    for (let i = 0; i < bytes.length; i++) {
      if (this.pushByte(bytes[i])) {
        pushed++;
      }
    }
    return pushed;
  }

  /**
   * Push single byte (Python pattern: check full with bit-mask)
   * @returns true if pushed, false if buffer full
   */
  pushByte(value: number): boolean {
    const nextHead = (this.head + 1) & this.mask; // Python pattern: bit-mask modulo
    
    if (nextHead === this.tail) {
      // Buffer full (Python pattern: tmpHead == tail)
      this.droppedBytes++;
      return false;
    }
    
    this.buffer[this.head] = value & 0xff;
    this.head = nextHead;
    return true;
  }

  /**
   * Pop single byte (Python pattern: check empty, then increment tail)
   * @returns byte value or null if empty
   */
  popByte(): number | null {
    if (this.tail === this.head) {
      // Buffer empty (Python pattern: tail == head)
      return null;
    }
    
    const byte = this.buffer[this.tail];
    this.tail = (this.tail + 1) & this.mask; // Python pattern: bit-mask modulo
    return byte;
  }

  /**
   * Peek at byte at offset without removing (Python pattern: buffer[tail + offset])
   */
  get(offset: number): number | undefined {
    const available = this.available();
    if (offset < 0 || offset >= available) {
      return undefined;
    }
    return this.buffer[(this.tail + offset) & this.mask]; // Python pattern: bit-mask
  }

  /**
   * Discard bytes from head (Python pattern: move tail forward)
   */
  discard(count: number): void {
    if (count <= 0) return;
    
    const available = this.available();
    if (count >= available) {
      this.clear();
      return;
    }
    
    this.tail = (this.tail + count) & this.mask; // Python pattern: bit-mask
  }

  /**
   * Read and remove bytes (Python pattern: pop multiple)
   */
  read(count: number): Uint8Array {
    const available = this.available();
    const n = Math.min(count, available);
    const out = new Uint8Array(n);
    
    for (let i = 0; i < n; i++) {
      const byte = this.popByte();
      out[i] = byte !== null ? byte : 0;
    }
    
    return out;
  }

  /**
   * Peek at multiple bytes without removing (non-destructive read)
   */
  slice(start: number, count: number): Uint8Array {
    if (start < 0) start = 0;
    
    const available = this.available();
    if (start >= available || count <= 0) {
      return new Uint8Array(0);
    }
    
    const n = Math.min(count, available - start);
    const out = new Uint8Array(n);
    
    for (let i = 0; i < n; i++) {
      const val = this.get(start + i);
      out[i] = val !== undefined ? val : 0;
    }
    
    return out;
  }

  /**
   * Find first occurrence of byte value (Python pattern: search from offset)
   */
  find(value: number, startOffset: number = 0): number {
    if (startOffset < 0) startOffset = 0;
    
    const available = this.available();
    for (let i = startOffset; i < available; i++) {
      if (this.get(i) === value) {
        return i;
      }
    }
    
    return -1;
  }

  /**
   * Get buffer capacity
   */
  getCapacity(): number {
    return this.capacity;
  }
}
